import { useState } from 'react';
import PropTypes from 'prop-types';

import ButtonSwitch from '../../../atoms/button-switch';
import CardModule from '../../shared/card-module';
import ChartContainer from '../../../atoms/chart-container';
import TableBasic from '../../../atoms/table-basic';
import format from '../../../../utils/format';
import useMoneyMarketChart, {
  TYPES
} from '../.././../../stores/profile/modules/use-money-market-chart';
import { className } from '../../../../utils/GlobalCssProxy';

const LIQUID_ASSETS_PERIODS = {
  daily: { label: 'Daily' },
  weekly: { label: 'Weekly' }
};

const LIQUID_ASSETS_PERIODS_ARRAY = [LIQUID_ASSETS_PERIODS.daily, LIQUID_ASSETS_PERIODS.weekly];

const MoneyMarketChartBase = ({ renderHeader, renderBody, ...props }) => (
  <CardModule {...props}>
    <CardModule.Header>{renderHeader()}</CardModule.Header>
    <CardModule.Body>{renderBody()}</CardModule.Body>
  </CardModule>
);

MoneyMarketChartBase.propTypes = {
  renderHeader: PropTypes.func.isRequired,
  renderBody: PropTypes.func.isRequired
};

const MoneyMarketChartBody = ({ alt, description, fundNameOverride, tableCaption, type }) => {
  const {
    fundName,
    hoverDatum,
    onHoverChart,
    onHoverChartEnd,
    onHoverChartStart,
    onResizeChart,
    tableRows,
    src,
    valueFormat
  } = useMoneyMarketChart({ type });

  return (
    <>
      <p {...className('paragraph-standard', 'mt-n2')}>{description}</p>
      <div {...className('mr-3')}>
        <span {...className('label-dot', 'mr-2', 'bg-blue')} />
        <span {...className('text-muted')}>{fundNameOverride || format.text(fundName)}</span>
        <div {...className('font-weight-bold', 'pl-4', 'font-size-large')}>
          <span {...className('color-blue')}>{format.date(hoverDatum.date)}</span>
          <span {...className('text-secondary', 'ml-3')}>
            Value:{' '}
            {format.number(hoverDatum.value, {
              formatString: valueFormat
            })}
          </span>
        </div>
      </div>
      <div {...className('mb-5', 'mt-5')}>
        <ChartContainer
          alt={`${alt}`}
          src={src}
          onHover={onHoverChart}
          onHoverEnd={onHoverChartEnd}
          onHoverStart={onHoverChartStart}
          onResize={onResizeChart}
        />
        {tableRows && tableRows.length ? (
          <div {...className('sr-only')}>
            <TableBasic>
              <caption>{tableCaption}</caption>
              <tbody>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Value</th>
                </tr>
                {tableRows.map((row, index) => {
                  return (
                    <tr data-key={`${row.date || ''}-${index}`} key={`${row.date || ''}-${index}`}>
                      <td>{format.date(row.date)}</td>
                      <td>{format.number(row.value)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </TableBasic>
          </div>
        ) : null}
      </div>
    </>
  );
};

MoneyMarketChartBody.propTypes = {
  alt: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  fundNameOverride: PropTypes.string,
  tableCaption: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(TYPES)),
  valueFormat: PropTypes.string
};

const LiquidAssets = ({ dailyDisclosure, weeklyDisclosure }) => {
  const [periodSelected, setPeriodSelected] = useState(LIQUID_ASSETS_PERIODS.daily);
  const isDaily = periodSelected === LIQUID_ASSETS_PERIODS.daily;
  const header = isDaily ? 'Daily Liquid Assets' : 'Weekly Liquid Assets';
  const periodString = isDaily ? 'Daily' : 'Weekly';
  const type = isDaily ? TYPES.liquidAssetsDaily : TYPES.liquidAssetsWeekly;
  const defaultDisclosure =
    'Regulations require that money market investments hold at least 10% of its total assets in daily liquid assets that can be readily converted to cash in one business day.';

  LiquidAssets.propTypes = {
    dailyDisclosure: PropTypes.string,
    weeklyDisclosure: PropTypes.string
  };

  return (
    <MoneyMarketChartBase
      data-analytics-componenttype="twothirds-width-component"
      data-analytics-id="TTW_LiquidAssets"
      data-analytics-section="component"
      renderHeader={() => (
        <>
          {header}
          <ButtonSwitch
            options={LIQUID_ASSETS_PERIODS_ARRAY}
            onChange={setPeriodSelected}
            selected={periodSelected}
          />
        </>
      )}
      renderBody={() => (
        <MoneyMarketChartBody
          alt={`${periodString} liquid assets chart`}
          description={
            isDaily ? dailyDisclosure || defaultDisclosure : weeklyDisclosure || defaultDisclosure
          }
          tableCaption={`${periodString} liquid assets data`}
          type={type}
        />
      )}
    />
  );
};

const NetFlows = ({ disclosure }) => {
  NetFlows.propTypes = {
    disclosure: PropTypes.string
  };
  return (
    <MoneyMarketChartBase
      data-analytics-componenttype="half-width-component"
      data-analytics-id="HW_NetFlows"
      data-analytics-section="component"
      renderHeader={() => 'Daily Net Shareholder Flows'}
      renderBody={() => (
        <MoneyMarketChartBody
          alt={`Daily net shareholder flows chart`}
          description={
            disclosure ||
            'The daily net shareholder flow represents the inflows minus the outflows over time for the given investment.'
          }
          fundNameOverride="Overall Value"
          tableCaption="Net shareholder flows data"
          type={TYPES.netFlows}
        />
      )}
    />
  );
};

const MarketValueDaily = ({ disclosure }) => {
  MarketValueDaily.propTypes = {
    disclosure: PropTypes.string
  };
  return (
    <MoneyMarketChartBase
      data-analytics-componenttype="half-width-component"
      data-analytics-id="HW_MarketValueDaily"
      data-analytics-section="component"
      renderHeader={() => 'Daily Market Value'}
      renderBody={() => (
        <MoneyMarketChartBody
          alt={`Daily market value chart`}
          description={
            disclosure ||
            "Daily market value is the market value of the fund's assets after liabilities are subtracted, expressed on a per-share basis to 1/100 of a penny."
          }
          tableCaption="Daily market value data"
          type={TYPES.marketValueDaily}
        />
      )}
    />
  );
};

const moneyMarketChart = {
  LiquidAssets,
  MarketValueDaily,
  NetFlows
};

export default moneyMarketChart;
